import React from 'react';
import { Link } from 'react-router-dom';
import { useGuidesBase } from '../../hooks/useGuidesBase';
import { Box, Title, Paper, Text, Stack, Group, Badge, Anchor, Alert, ThemeIcon, Breadcrumbs, SimpleGrid, Progress } from '@mantine/core';
import { Lightbulb, Check, Warning, Gauge, DeviceMobile, MagnifyingGlass, MouseSimple } from '@phosphor-icons/react';
import SEO from '../../components/SEO';

function ScoreCard({ icon, title, description, good, bad }) {
  const Icon = icon;
  const guidesBase = useGuidesBase();
  return (
    <Paper withBorder p="md" radius="md">
      <Group gap="sm" mb="sm">
        <ThemeIcon variant="light" color="brand" size="md" radius="md">
          <Icon size={16} weight="bold" />
        </ThemeIcon>
        <Text size="sm" fw={600}>{title}</Text>
      </Group>
      <Text size="xs" c="dimmed" mb="sm" lh={1.5}>{description}</Text>
      <Stack gap={4}>
        <Group gap="xs">
          <ThemeIcon variant="light" color="green" size="xs" radius="xl"><Check size={8} weight="bold" /></ThemeIcon>
          <Text size="xs" c="green.7">{good}</Text>
        </Group>
        <Group gap="xs">
          <ThemeIcon variant="light" color="red" size="xs" radius="xl"><Warning size={8} weight="bold" /></ThemeIcon>
          <Text size="xs" c="red.6">{bad}</Text>
        </Group>
      </Stack>
    </Paper>
  );
}

export default function LandingPageGuide() {
  const guidesBase = useGuidesBase();
  return (
    <Box px="lg" py="md">
      <SEO
        title="Optymalizacja stron docelowych dla Google Ads"
        description="Szybkość ładowania, Quality Score, CTA. Jak zoptymalizować strony docelowe, aby obniżyć CPC i zwiększyć konwersje z Google Ads."
        path={`${guidesBase}/landing-pages`}
        noIndex={guidesBase !== '/poradniki'}
      />
      <Breadcrumbs mb="sm" styles={{ separator: { color: 'var(--mantine-color-gray-4)' } }}>
        <Anchor component={Link} to={guidesBase} size="sm">Przewodniki</Anchor>
        <Text size="sm" c="dark">Optymalizacja stron docelowych</Text>
      </Breadcrumbs>
      <Title order={3} mb="md">Optymalizacja stron docelowych</Title>

      <Alert color="blue" mb="md" icon={<Lightbulb size={16} />}>
        <Text size="sm">Strona docelowa bezpośrednio wpływa na Quality Score, koszt kliknięcia i współczynnik konwersji. Poprawa strony to najszybszy sposób na obniżenie CPC.</Text>
      </Alert>

      {/* Quality Score */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Jak Google ocenia stronę docelową</Text>
        <Text size="sm" c="dimmed" lh={1.6} mb="md">
          Quality Score (1-10) składa się z trzech elementów. Strona docelowa wpływa na "Landing Page Experience":
        </Text>
        <Stack gap="sm">
          {[
            { label: 'Trafność reklamy (Ad Relevance)', value: 33 },
            { label: 'Jakość strony docelowej (Landing Page)', value: 33 },
            { label: 'Przewidywany CTR', value: 33 },
          ].map((item, i) => (
            <Box key={i}>
              <Group justify="space-between" mb={2}>
                <Text size="xs">{item.label}</Text>
                <Text size="xs" c="dimmed">{item.value}%</Text>
              </Group>
              <Progress value={item.value} size="sm" color="brand" />
            </Box>
          ))}
        </Stack>
        <Text size="xs" c="dimmed" mt="sm">Quality Score 7+ obniża CPC nawet o 50%. Score poniżej 4 może podnieść CPC o 400%.</Text>
      </Paper>

      {/* Key factors */}
      <Text size="sm" fw={600} mb="sm">Kluczowe czynniki</Text>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" mb="md">
        <ScoreCard
          icon={Gauge}
          title="Szybkość ładowania"
          description="Google mierzy Core Web Vitals: LCP, FID, CLS. Strona powinna załadować się w < 3 sekundy."
          good="LCP < 2.5s, kompresja obrazów, CDN"
          bad="LCP > 4s, duże obrazy, wolny hosting"
        />
        <ScoreCard
          icon={DeviceMobile}
          title="Wersja mobilna"
          description="60-80% kliknięć w Google Ads pochodzi z urządzeń mobilnych. Strona MUSI działać na telefonie."
          good="Responsywny design, duże przyciski CTA"
          bad="Desktop-only, małe elementy, popup na mobile"
        />
        <ScoreCard
          icon={MagnifyingGlass}
          title="Trafność treści"
          description="Treść strony musi odpowiadać zapytaniu użytkownika i treści reklamy."
          good="Nagłówek = słowo kluczowe, spójna oferta"
          bad="Ogólna strona główna, brak dopasowania"
        />
        <ScoreCard
          icon={MouseSimple}
          title="Call-to-Action (CTA)"
          description="Użytkownik musi wiedzieć co zrobić na stronie. CTA powinno być widoczne bez scrollowania."
          good="Widoczny przycisk, formularz above the fold"
          bad="CTA na dole strony, niejasna akcja"
        />
      </SimpleGrid>

      {/* Checklist */}
      <Paper withBorder p="lg" radius="md" mb="md">
        <Text size="sm" fw={600} mb="sm">Checklista optymalizacji</Text>
        <Stack gap="xs">
          {[
            'Nagłówek H1 zawiera główne słowo kluczowe',
            'CTA (przycisk/formularz) widoczny bez scrollowania',
            'Strona ładuje się w mniej niż 3 sekundy (test: PageSpeed Insights)',
            'Wersja mobilna działa poprawnie (test: Mobile-Friendly Test)',
            'Certyfikat SSL (https://)',
            'Brak wyskakujących okienek (popup) blokujących treść',
            'Dane kontaktowe widoczne (telefon, adres, formularz)',
            'Opinie klientów / dowody społeczne',
            'Jasna propozycja wartości w pierwszych 5 sekundach',
            'Spójność: treść reklamy = treść strony docelowej',
          ].map((item, i) => (
            <Group key={i} gap="xs" wrap="nowrap">
              <ThemeIcon variant="light" color="green" size="xs" radius="xl"><Check size={10} weight="bold" /></ThemeIcon>
              <Text size="sm">{item}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>

      {/* Tools */}
      <Paper withBorder p="lg" radius="md">
        <Text size="sm" fw={600} mb="sm">Narzędzia do testowania</Text>
        <Stack gap="xs">
          {[
            { name: 'PageSpeed Insights', url: 'https://pagespeed.web.dev/', desc: 'Szybkość i Core Web Vitals' },
            { name: 'Mobile-Friendly Test', url: 'https://search.google.com/test/mobile-friendly', desc: 'Sprawdzenie wersji mobilnej' },
            { name: 'GTmetrix', url: 'https://gtmetrix.com/', desc: 'Szczegółowa analiza wydajności' },
          ].map((tool, i) => (
            <Group key={i} gap="xs">
              <Anchor href={tool.url} target="_blank" size="sm" fw={500}>{tool.name}</Anchor>
              <Text size="xs" c="dimmed">{tool.desc}</Text>
            </Group>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}
